import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalizationModule, LocalizationService, SubscriptionService } from '@abp/ng.core';
import { inject, Injector } from '@angular/core';
import {
  AgGridComponent,
  AuditedColumns,
  BaseDapperListPage,
  DwGridDataSourceService,
  FieldTypes,
  ListPageComponent,
} from '@digiwall/ag-grid';
import { ColDef } from '@ag-grid-community/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { PaymentMethodTypeStore } from '@digiwall/goliath-contacts';
import { IDapperReadDeleteEntityService, IUserStoreToken, LanguageStore } from '@digiwall/core';
import { StockDocDto, StockDocListDto } from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentEntityNames } from '../../constants/entity-names.constants';
import { DialogBoxCloneComponent } from '../dialog-box-clone/dialog-box-clone.component';
import { CommonModule } from '@angular/common';
import { GoliathDocumentsEnumerationTypeStore } from '../../enumeration-types/enumeration-type.store';
import { IDocumentService } from '../model/document-service';

export const StockDocListComponentProviders = [DwGridDataSourceService, SubscriptionService];
export const StockDocListComponentImports = [
  CommonModule,
  ListPageComponent,
  AgGridComponent,
  LocalizationModule,
];

export abstract class StockDocListComponent<
  TListDto extends StockDocListDto,
  TEntityDto extends StockDocDto,
  TService extends IDapperReadDeleteEntityService<TListDto> & IDocumentService<TEntityDto>,
> extends BaseDapperListPage<TListDto, TService> {
  documentEnumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  paymentMethodTypeStore = inject(PaymentMethodTypeStore);
  languageStore = inject(LanguageStore);
  userStore = inject(IUserStoreToken);
  protected modalService = inject(NgbModal);
  protected GoliathDocumentEntityNames: any = GoliathDocumentEntityNames;

  override colDefs: ColDef[] = [
    ...AuditedColumns(this.localizationService),
    {
      field: 'documentDate',
      headerName: this.localizationService.instant('Doc::CustomerQuote:DocumentDate'),
      type: FieldTypes.Date,
    },
    {
      field: 'displayNumber',
      headerName: this.localizationService.instant('::Number'),
      type: FieldTypes.String,
    },
    {
      field: 'isClosed',
      headerName: this.localizationService.instant('Doc::CustomerQuote:IsClosed'),
      type: FieldTypes.Boolean,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
    },
    {
      field: 'note',
      headerName: this.localizationService.instant('::Note'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      colId: 'dayBook',
      field: 'dayBook.current',
      headerName: this.localizationService.instant('Doc::DayBook'),
      type: FieldTypes.String,
    },
    {
      field: 'salesPerson',
      headerName: this.localizationService.instant('::Responsible'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'currencyCode',
      headerName: this.localizationService.instant('::Currency'),
      type: FieldTypes.String,
    },
    {
      field: 'companySite',
      headerName: this.localizationService.instant('::CompanySite'),
      type: FieldTypes.String,
    },
    {
      field: 'warehouseName',
      headerName: this.localizationService.instant('Doc::StockDoc:Warehouse'),
      type: FieldTypes.String,
    },
  ];

  constructor(
    protected docService: TService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected confirmation: ConfirmationService,
    protected localizationService: LocalizationService,
    protected injector: Injector,
    subscriptionService: SubscriptionService,
    datasource: DwGridDataSourceService,
  ) {
    super(docService, route, router, confirmation, subscriptionService, datasource);
  }

  private duplicateDoc(includeAnnexes: boolean) {
    const modalRef = this.modalService.open(DialogBoxCloneComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      scrollable: true,
      injector: this.injector,
      size: 'l',
    });

    modalRef.shown.subscribe(_ => {
      modalRef.componentInstance.documentId = this.selectedEntities()[0].id;
      modalRef.componentInstance.docService = this.docService;
      modalRef.componentInstance.areAnnexesIncluded = includeAnnexes;
      modalRef.componentInstance.fromList = true;
    });
  }

  duplicateStockDoc() {
    this.docService.getAnnexesCount(this.selectedEntities()[0].id).subscribe(count => {
      this.duplicateDoc(count != 0);
    });
  }
}
